import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  X,
  GripVertical,
  Save,
  Play,
  RotateCcw
} from 'lucide-react';
import { galleryService } from '@/services/galleryService';
import { isVideoUrl } from '../../utils/imageUrl';
import { Button } from '@/components/ui/Button/Button';
import { GalleryAsset } from '@/types/gallery';

interface AssetSequencerProps {
  galleryId: number;
  assets: GalleryAsset[];
  onClose: () => void;
  onSequenceUpdated: () => void;
}

export const AssetSequencer: React.FC<AssetSequencerProps> = ({
  galleryId,
  assets,
  onClose,
  onSequenceUpdated
}) => {
  const [orderedAssets, setOrderedAssets] = useState<GalleryAsset[]>(() => 
    [...assets].sort((a, b) => a.sequence_number - b.sequence_number)
  );
  const [draggedAsset, setDraggedAsset] = useState<GalleryAsset | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number>(-1);

  // Update sequence mutation
  const updateSequenceMutation = useMutation({
    mutationFn: (updates: Array<{ id: number; sequence_number: number }>) => 
      galleryService.reorderGalleryAssets(galleryId, updates),
    onSuccess: () => {
      onSequenceUpdated();
    }
  });

  const handleDragStart = (e: React.DragEvent, asset: GalleryAsset) => {
    setDraggedAsset(asset);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(-1);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(-1);

    if (!draggedAsset) return;

    const dragIndex = orderedAssets.findIndex(asset => asset.id === draggedAsset.id);
    if (dragIndex === dropIndex) return;

    const newOrderedAssets = [...orderedAssets];
    const [movedAsset] = newOrderedAssets.splice(dragIndex, 1);
    newOrderedAssets.splice(dropIndex, 0, movedAsset);

    setOrderedAssets(newOrderedAssets);
    setDraggedAsset(null);
  };

  const handleDragEnd = () => {
    setDraggedAsset(null);
    setDragOverIndex(-1);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    
    const newOrderedAssets = [...orderedAssets];
    [newOrderedAssets[index - 1], newOrderedAssets[index]] = 
    [newOrderedAssets[index], newOrderedAssets[index - 1]];
    
    setOrderedAssets(newOrderedAssets);
  };

  const handleMoveDown = (index: number) => {
    if (index === orderedAssets.length - 1) return;
    
    const newOrderedAssets = [...orderedAssets];
    [newOrderedAssets[index], newOrderedAssets[index + 1]] = 
    [newOrderedAssets[index + 1], newOrderedAssets[index]];
    
    setOrderedAssets(newOrderedAssets);
  };

  const handleReset = () => {
    setOrderedAssets([...assets].sort((a, b) => a.sequence_number - b.sequence_number));
  };

  const handleSave = () => {
    const updates = orderedAssets.map((asset, index) => ({
      id: asset.id,
      sequence_number: index + 1
    }));

    updateSequenceMutation.mutate(updates);
  };

  const hasChanges = orderedAssets.some((asset, index) => 
    asset.sequence_number !== index + 1
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-rgf-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-rgf-gray-900">
                Reorder Gallery Assets
              </h3>
              <p className="mt-1 text-sm text-rgf-gray-500">
                Drag and drop or use buttons to change the display order of assets
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              icon={<X className="h-4 w-4" />}
            />
          </div>

          {/* Instructions */}
          <div className="bg-rgf-primary-50 border border-rgf-primary-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-rgf-primary-800">
              <strong>Tip:</strong> Drag assets using the grip handle or use the arrow buttons to reorder them. 
              The sequence number shows the display order in the gallery.
            </p>
          </div>

          {/* Asset List */}
          <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
            {orderedAssets.map((asset, index) => {
              const isDragging = draggedAsset?.id === asset.id;
              const isDropTarget = dragOverIndex === index;
              
              return (
                <div
                  key={asset.id}
                  className={`flex items-center space-x-4 p-4 bg-white border rounded-lg transition-all duration-200 ${
                    isDragging ? 'opacity-50 scale-95' : ''
                  } ${
                    isDropTarget ? 'border-rgf-primary-400 bg-rgf-primary-50' : 'border-rgf-gray-200'
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, asset)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  {/* Drag Handle */}
                  <div className="cursor-grab active:cursor-grabbing text-rgf-gray-400 hover:text-rgf-gray-600">
                    <GripVertical className="h-5 w-5" />
                  </div>

                  {/* Sequence Number */}
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-rgf-primary-100 text-rgf-primary-700 rounded-full text-sm font-medium">
                      {index + 1}
                    </span>
                  </div>

                  {/* Asset Thumbnail */}
                  <div className="flex-shrink-0 relative">
                    {isVideoUrl(asset.cdn_url) ? (
                      <>
                        <video
                          src={asset.cdn_url}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-16 h-16 object-cover rounded-lg"
                          onError={(e) => {
                            (e.target as HTMLVideoElement).style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="h-4 w-4 text-white drop-shadow" />
                        </div>
                      </>
                    ) : (
                      <img
                        src={asset.cdn_url}
                        alt={asset.asset_id}
                        className="w-16 h-16 object-cover rounded-lg"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (!img.dataset.fallback) {
                            img.dataset.fallback = '1';
                            img.src = '/placeholder-image.svg';
                          }
                        }}
                      />
                    )}
                  </div>

                  {/* Asset Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-rgf-gray-900 truncate">
                      Asset ID: {asset.asset_id}
                    </p>
                    <p className="text-xs text-rgf-gray-500">
                      Original position: {asset.sequence_number}
                      {asset.sequence_number !== index + 1 && (
                        <span className="text-orange-600 ml-2">
                          → New position: {index + 1}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Move Buttons */}
                  <div className="flex flex-col space-y-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="!p-1"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="!p-1"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === orderedAssets.length - 1}
                      title="Move down"
                    >
                      ↓
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-rgf-gray-200">
            <Button
              variant="secondary"
              onClick={handleReset}
              disabled={!hasChanges}
              icon={<RotateCcw className="h-4 w-4" />}
            >
              Reset to Original
            </Button>

            <div className="flex items-center space-x-3">
              <Button
                variant="secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges}
                loading={updateSequenceMutation.isPending}
                icon={<Save className="h-4 w-4" />}
              >
                Save New Order
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};